import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "../hooks/useLenis";

const LOGO =
    "https://customer-assets-jt897jd0.emergentagent.net/job_b27ff44d-5001-4718-90bb-7f512a6d425a/artifacts/bkw34fsr_4B0C15C0-DB1B-4AE6-B2B8-3386D3826D16.png";

const LINKS = [
    { label: "Services", id: "services" },
    { label: "Who", id: "audience" },
    { label: "Work", id: "work" },
    { label: "Process", id: "process" },
    { label: "About", id: "about" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24 });

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToSection(id), open ? 350 : 0);
    };

    return (
        <>
            <motion.div
                className="fixed inset-x-0 top-0 z-[95] h-[2px] origin-left bg-gradient-to-r from-sky-400 to-[#0066FF]"
                style={{ scaleX: progress }}
            />
            <motion.header
                initial={{ y: -90, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 2.5, ease: [0.22, 1, 0.36, 1] }}
                className={`fixed inset-x-0 top-0 z-[90] transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled ? "glass border-b border-white/10" : "bg-transparent border-b border-transparent"
                }`}
            >
                <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
                    <button
                        data-testid="nav-brand-logo"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="flex items-center gap-3"
                        aria-label="Footman Digital Solutions — back to top"
                    >
                        <img src={LOGO} alt="" className="h-10 w-10 rounded-lg" />
                        <span className="text-left leading-none">
                            <span className="block font-display text-sm font-extrabold tracking-[0.18em] text-[#0A0F1D]">FOOTMAN</span>
                            <span className="block font-mono text-[9px] uppercase tracking-[0.32em] text-sky-400">Digital Solutions</span>
                        </span>
                    </button>

                    <div className="hidden items-center gap-8 lg:flex">
                        {LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="group relative font-mono text-[11px] uppercase tracking-[0.28em] text-slate-600 transition-colors duration-300 hover:text-[#0066FF]"
                            >
                                {l.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-sky-400 transition-[width] duration-300 group-hover:w-full" />
                            </button>
                        ))}
                        <button
                            data-testid="nav-cta-start"
                            onClick={() => go("contact")}
                            className="group inline-flex items-center gap-2 rounded-full bg-[#0066FF] px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_24px_rgba(0,102,255,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-[#1a75ff] hover:shadow-[0_0_40px_rgba(0,102,255,0.5)]"
                        >
                            Start Your Website
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                    </div>

                    <button
                        data-testid="nav-mobile-toggle"
                        className="flex h-11 w-11 items-center justify-center rounded-full glass lg:hidden"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        className="fixed inset-0 z-[85] flex flex-col justify-center bg-[#F7F9FC]/97 px-8 backdrop-blur-xl lg:hidden"
                        initial={{ opacity: 0, y: -24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {[...LINKS, { label: "Contact", id: "contact" }].map((l, i) => (
                            <motion.button
                                key={l.id}
                                data-testid={`nav-mobile-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="border-b border-white/10 py-5 text-left font-display text-3xl font-bold uppercase tracking-wide text-[#0A0F1D] transition-colors duration-300 hover:text-[#0066FF]"
                                initial={{ opacity: 0, x: -28 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {l.label}
                            </motion.button>
                        ))}
                        <motion.p
                            className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            Your Vision. Online.
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
